import { useEffect } from 'react';

import classNames from 'classnames';
import { useProjectDetail } from 'modules/data';
import { useI18n } from 'modules/i18n';
import { TapeAndTime } from 'modules/interview-helpers';
import { LinkOrA, useProject } from 'modules/routes';
import { convertLegacyQuery, useFacets } from 'modules/search';
import { isMobile } from 'modules/user-agent';
import { formatTimecode } from 'modules/utils';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import queryToFacets from '../queryToFacets';
import WorkbookActions from './WorkbookActions';

/**
 * Loads an entry's project through Redux and links to its saved interview or search.
 * Entries remain visible when project details are unavailable.
 * Shows entry controls only after the current project loads and is confirmed
 * to be an individual project.
 *
 * @param {Object} props - Entry properties and existing interview actions.
 * @param {Object} props.data - Saved workbook entry, including its project ID.
 * @param {string} [props.className] - Additional styling.
 * @param {Object} props.statuses - Request statuses for related interviews.
 * @param {Object} props.interviews - Loaded interviews by archive ID.
 * @param {Function} props.setArchiveId - Selects the interview for navigation.
 * @param {Function} props.sendTimeChangeRequest - Seeks to a saved segment.
 * @param {Function} props.hideSidebar - Closes the mobile sidebar.
 * @param {Function} props.fetchData - Loads a related interview when needed.
 * @returns {React.ReactElement} Workbook entry and actions.
 */
export default function WorkbookItem({
    data,
    className,
    statuses,
    interviews,
    setArchiveId,
    sendTimeChangeRequest,
    hideSidebar,
    fetchData,
}) {
    const { t, locale } = useI18n();
    const { project } = useProjectDetail(data.project_id);
    const { facets } = useFacets();
    const {
        project: currentProject,
        isLoading: currentProjectLoading,
        error: currentProjectError,
    } = useProject();
    const projectId = project?.shortname;
    const interview = interviews[data.media_id];

    const fetchingInterview = !!statuses['interviews'][data.media_id];

    useEffect(() => {
        if (data.media_id && project && !interview && !fetchingInterview) {
            fetchData(
                { projectId, locale, project },
                'interviews',
                data.media_id,
                'transcript_coupled'
            );
        }
    }, [
        data.media_id,
        fetchData,
        fetchingInterview,
        interview,
        locale,
        project,
        projectId,
    ]);

    function hideSidebarIfMobile() {
        if (isMobile()) {
            hideSidebar();
        }
    }

    function handleLinkClick() {
        if (data.type === 'InterviewReference') {
            setArchiveId(data.media_id);
        } else if (data.type === 'UserAnnotation') {
            setArchiveId(data.properties.interview_archive_id);
            interview?.transcript_coupled &&
                sendTimeChangeRequest(
                    data.properties.tape_nbr,
                    data.properties.time
                );
        }

        hideSidebarIfMobile();
    }

    const callKey =
        'call' +
        data.type.replace(/([A-Z])/g, function ($1) {
            return '_' + $1.toLowerCase();
        });
    let itemPath;
    if (project) {
        switch (data.type) {
            case 'InterviewReference':
                itemPath = `interviews/${data.media_id}`;
                break;
            case 'Search':
                itemPath = `searches/archive?${queryString.stringify(convertLegacyQuery(data.properties), { arrayFormat: 'bracket' })}`;
                break;
            case 'UserAnnotation':
                itemPath = `interviews/${data.properties.interview_archive_id}?tape=${data.properties.tape_nbr}&time=${formatTimecode(data.properties.time, true)}`;
                break;
            default:
        }
    }

    const facetValues = queryToFacets(
        convertLegacyQuery(data.properties),
        facets,
        locale
    );
    const date = new Date(data.created_at).toLocaleDateString(locale, {
        dateStyle: 'medium',
    });

    return (
        <div className={classNames('WorkbookEntry', className)}>
            <h3 className="WorkbookEntry-title">{data.title}</h3>

            <dl className="WorkbookEntry-list">
                {data.description && (
                    <div className="WorkbookEntry-listItem">
                        <dt>{t('modules.workbook.note')}</dt>
                        <dd>{data.description}</dd>
                    </div>
                )}
                {data.type === 'Search' && data.properties.fulltext && (
                    <div className="WorkbookEntry-listItem">
                        <dt>{t('modules.workbook.full_text_search')}</dt>
                        <dd>{data.properties.fulltext}</dd>
                    </div>
                )}
                {data.type === 'Search' && facetValues.length > 0 && (
                    <div className="WorkbookEntry-listItem">
                        <dt>{t('modules.workbook.filter')}</dt>
                        <dd>{facetValues}</dd>
                    </div>
                )}
                {data.type === 'UserAnnotation' && (
                    <div className="WorkbookEntry-listItem">
                        <dt>{t('modules.workbook.segment')}</dt>
                        <dd>
                            <TapeAndTime
                                tape={data.properties.tape_nbr}
                                time={data.properties.time}
                                transcriptCoupled={
                                    interview?.transcript_coupled
                                }
                            />
                        </dd>
                    </div>
                )}
                <div className="WorkbookEntry-listItem">
                    <dt>{t('modules.workbook.from')}</dt>
                    <dd>{date}</dd>
                </div>
            </dl>

            <p className="WorkbookEntry-link">
                {project ? (
                    <LinkOrA
                        project={project}
                        to={itemPath}
                        onLinkClick={handleLinkClick}
                    >
                        {t(callKey)}
                    </LinkOrA>
                ) : (
                    t('modules.workbook.project_unavailable')
                )}
            </p>

            {currentProject &&
                !currentProjectLoading &&
                !currentProjectError &&
                !currentProject.is_umbrella && (
                    <WorkbookActions
                        item={data}
                        itemPath={itemPath}
                        className="WorkbookEntry-actions"
                    />
                )}
        </div>
    );
}

WorkbookItem.propTypes = {
    data: PropTypes.object.isRequired,
    statuses: PropTypes.object.isRequired,
    interviews: PropTypes.object.isRequired,
    className: PropTypes.string,
    setArchiveId: PropTypes.func.isRequired,
    sendTimeChangeRequest: PropTypes.func.isRequired,
    hideSidebar: PropTypes.func.isRequired,
    fetchData: PropTypes.func.isRequired,
};
